import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import React, { useContext } from 'react';
import { v4 } from 'uuid';
import { CompanyContext } from '../context/CompanyContext';
import { ProductTransactionContext, ProductTransactionContextProps } from '../context/ProductTransactionContext';
import {
  GetProductTransactionsResponse,
  GetProductTransactionsVariables,
  ProductTransactionQueries,
} from '../graphql/productTransaction.graphql';
import useLazyQuery from '../hooks/useLazyQuery';
import { ProductTransaction } from '../types/productTransaction';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';

const state = {
  items: new Map<string, ProductTransaction>(),
  loading: true,
};

export const productTransactionSlice = createSlice({
  name: 'productTransaction',
  initialState: { key: v4() },
  reducers: {
    reset: rx => {
      state.items = new Map();
      state.loading = true;
      rx.key = v4();
    },
    set: (rx, action: PayloadAction<ProductTransaction[]>) => {
      state.items = new Map();
      action.payload.forEach(pt => state.items.set(pt.id, new ProductTransaction(pt)));
      rx.key = v4();
    },
    add: (rx, action: PayloadAction<ProductTransaction[]>) => {
      action.payload.forEach(pt => state.items.set(pt.id, new ProductTransaction(pt)));
      state.items = new Map(state.items);
      rx.key = v4();
    },
    delete: (rx, action: PayloadAction<ProductTransaction[]>) => {
      action.payload.forEach(pt => state.items.delete(pt.id));
      state.items = new Map(state.items);
      rx.key = v4();
    },
    setLoading: (rx, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      rx.key = v4();
    },
  },
});

interface ProductTransactionProviderProps {
  children: React.ReactNode;
}

export default function ProductTransactionProvider({ children }: ProductTransactionProviderProps) {
  const { currentCompany } = useContext(CompanyContext);

  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.productTransactions.key);

  const [loadAllProductTransactions, { loading: loadAllProductTransactionsLoading }] = useLazyQuery<
    GetProductTransactionsResponse,
    GetProductTransactionsVariables
  >(ProductTransactionQueries.get, {
    variables: { companyId: currentCompany.companyId, since: new Date(0) },
    onCompleted: async response => {
      dispatch(productTransactionSlice.actions.add(response.productTransactions.data));
      if (response.productTransactions.hasNext === true) {
        await loadAllProductTransactions({
          variables: {
            companyId: currentCompany.companyId,
            page: ++response.productTransactions.page,
            since: new Date(0),
          },
        });
      }
    },
  });

  const productTransactionContext: ProductTransactionContextProps = {
    productTransactions: state.items,
    productTransactionsLoading: state.loading || loadAllProductTransactionsLoading,
    setProductTransactions: (productTransactions: Map<string, ProductTransaction>) =>
      dispatch(productTransactionSlice.actions.set([...productTransactions.values()])),
    loadAllProductTransactions,
  };

  return (
    <ProductTransactionContext.Provider value={productTransactionContext}>
      {children}
    </ProductTransactionContext.Provider>
  );
}
